import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Badge, Spinner, ListGroup } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrash, FaEdit, FaPrint, FaShoppingCart } from 'react-icons/fa';
import axios from 'axios';

const BestellungDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bestellung, setBestellung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Status-Farben für die Badges
  const statusVariants = {
    'ausstehend': 'warning',
    'bestellt': 'info',
    'geliefert': 'success',
    'storniert': 'danger'
  };

  // Bestellung laden
  useEffect(() => {
    const fetchBestellung = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/api/bestellungen/${id}`);
        setBestellung(response.data);
      } catch (err) {
        console.error('Fehler beim Laden der Bestellung:', err);
        setError('Die Bestellung konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchBestellung();
  }, [id]);

  // Bestellung löschen
  const handleDelete = async () => {
    if (window.confirm(`Möchten Sie die Bestellung "${bestellung.bestellnummer}" wirklich löschen?`)) {
      try {
        await axios.delete(`/api/bestellungen/${id}`);
        navigate('/bestellungen', { state: { message: `Bestellung "${bestellung.bestellnummer}" wurde gelöscht.` } });
      } catch (err) {
        console.error('Fehler beim Löschen der Bestellung:', err);
        setError('Die Bestellung konnte nicht gelöscht werden.');
      }
    }
  };

  // Status aktualisieren
  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`/api/bestellungen/${id}/status`, { status: newStatus });
      const response = await axios.get(`/api/bestellungen/${id}`);
      setBestellung(response.data);
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Status:', err);
      setError('Der Status konnte nicht aktualisiert werden.');
    }
  };

  // Artikel zum Warenkorb hinzufügen
  const addToCart = async (artikelId, menge) => {
    try {
      await axios.post('/api/warenkorb', { artikelId, menge });
      alert('Artikel wurde zum Warenkorb hinzugefügt.');
    } catch (err) {
      console.error('Fehler beim Hinzufügen zum Warenkorb:', err);
      alert('Der Artikel konnte nicht zum Warenkorb hinzugefügt werden.');
    }
  };

  // Bestellung drucken (ein einfacher Ausdruck der Browser-Seite)
  const handlePrint = () => {
    window.print();
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Laden...</span>
        </Spinner>
        <p className="mt-2">Bestellung wird geladen...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          {error}
          <div className="mt-3">
            <Button as={Link} to="/bestellungen" variant="outline-primary">
              <FaArrowLeft className="me-2" /> Zurück zur Bestellungsübersicht
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!bestellung) {
    return (
      <Container>
        <Alert variant="warning">
          Bestellung nicht gefunden.
          <div className="mt-3">
            <Button as={Link} to="/bestellungen" variant="outline-primary">
              <FaArrowLeft className="me-2" /> Zurück zur Bestellungsübersicht
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="bestellung-detail">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Bestellung {bestellung.bestellnummer}</h1>
        <Button 
          as={Link} 
          to="/bestellungen" 
          variant="outline-secondary"
          className="d-none d-md-block"
        >
          <FaArrowLeft className="me-2" /> Zurück
        </Button>
      </div>
      
      {updateSuccess && (
        <Alert variant="success" dismissible onClose={() => setUpdateSuccess(false)}>
          Der Status wurde erfolgreich aktualisiert.
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col lg={8} className="mb-4 mb-lg-0">
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Bestellpositionen</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Artikel</th>
                      <th className="text-center">Menge</th>
                      <th className="text-right">Preis</th>
                      <th className="text-right">Summe</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bestellung.positionen && bestellung.positionen.map(position => (
                      <tr key={position.id}>
                        <td>
                          <div>
                            <Link to={`/artikel/${position.artikelId}`} className="text-decoration-none fw-bold">
                              {position.name}
                            </Link>
                            <div className="small text-muted">
                              Art.-Nr.: {position.artikelnummer}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          {position.menge} {position.einheit}
                        </td>
                        <td className="text-end">
                          {position.preis.toFixed(2)} €
                        </td>
                        <td className="text-end fw-bold">
                          {(position.preis * position.menge).toFixed(2)} €
                        </td>
                        <td className="text-end">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => addToCart(position.artikelId, position.menge)}
                            title="Zum Warenkorb hinzufügen"
                          >
                            <FaShoppingCart />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-light">
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">Gesamtbetrag:</td>
                      <td className="text-end fw-bold">{bestellung.gesamtpreis.toFixed(2)} €</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Button 
            as={Link} 
            to="/bestellungen" 
            variant="outline-secondary"
            className="d-block d-md-none mb-4"
          >
            <FaArrowLeft className="me-2" /> Zurück zur Bestellungsübersicht
          </Button>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Bestelldetails</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Status:</Col>
                  <Col sm={8}>
                    <Badge bg={statusVariants[bestellung.status] || 'secondary'}>
                      {bestellung.status.charAt(0).toUpperCase() + bestellung.status.slice(1)}
                    </Badge>
                  </Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Datum:</Col>
                  <Col sm={8}>{formatDate(bestellung.datum)}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Gesamtpreis:</Col>
                  <Col sm={8}>{bestellung.gesamtpreis.toFixed(2)} €</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col sm={4} className="fw-bold">Erstellt am:</Col>
                  <Col sm={8}>{formatDate(bestellung.created_at)}</Col>
                </Row>
              </ListGroup.Item>
            </ListGroup>
          </Card>
          
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Status ändern</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="outline-warning" 
                  disabled={bestellung.status === 'ausstehend'}
                  onClick={() => updateStatus('ausstehend')}
                >
                  Ausstehend
                </Button>
                <Button 
                  variant="outline-info" 
                  disabled={bestellung.status === 'bestellt'}
                  onClick={() => updateStatus('bestellt')}
                >
                  Bestellt
                </Button>
                <Button 
                  variant="outline-success" 
                  disabled={bestellung.status === 'geliefert'}
                  onClick={() => updateStatus('geliefert')}
                >
                  Geliefert
                </Button>
                <Button 
                  variant="outline-danger" 
                  disabled={bestellung.status === 'storniert'}
                  onClick={() => updateStatus('storniert')}
                >
                  Storniert
                </Button>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Aktionen</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="outline-primary" 
                  className="d-flex align-items-center justify-content-center"
                  onClick={handlePrint}
                >
                  <FaPrint className="me-2" /> Bestellung drucken
                </Button>
                <Button 
                  variant="danger" 
                  className="d-flex align-items-center justify-content-center"
                  onClick={handleDelete}
                >
                  <FaTrash className="me-2" /> Bestellung löschen
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BestellungDetail;
